import { useState, useEffect } from "react";

interface CookieOptions {
  expires?: number; // dias
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
}

export function useCookie(key: string, defaultValue: string = "") {
  const [cookieValue, setCookieValue] = useState<string>(() => {
    if (typeof window === "undefined") return defaultValue;

    const cookies = document.cookie.split(";");
    const cookie = cookies.find((c) => c.trim().startsWith(`${key}=`));
    return cookie ? cookie.split("=")[1] : defaultValue;
  });

  const setCookie = (value: string, options: CookieOptions = {}) => {
    const {
      expires = 365, // 1 ano por padrão
      path = "/",
      domain,
      secure = false,
      sameSite = "lax",
    } = options;

    let cookieString = `${key}=${value}; path=${path}`;

    if (expires) {
      const date = new Date();
      date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
      cookieString += `; expires=${date.toUTCString()}`;
    }

    if (domain) {
      cookieString += `; domain=${domain}`;
    }

    if (secure) {
      cookieString += `; secure`;
    }

    cookieString += `; samesite=${sameSite}`;

    document.cookie = cookieString;
    setCookieValue(value);
  };

  const deleteCookie = () => {
    document.cookie = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    setCookieValue(defaultValue);
  };

  return [cookieValue, setCookie, deleteCookie] as const;
}

// Hook específico para o modal de novidades
export function useNewsModal() {
  const [hasSeenNews, setHasSeenNews, deleteNewsCookie] = useCookie(
    "eleg-news-seen",
    "false"
  );
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    // Verificar se o usuário já viu as novidades
    const hasSeen = hasSeenNews === "true";

    if (!hasSeen) {
      // Mostrar o modal após um pequeno delay para melhor UX
      const timer = setTimeout(() => {
        setShowModal(true);
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [hasSeenNews]);

  const handleCloseModal = () => {
    setShowModal(false);
    // Marcar como visto por 30 dias
    setHasSeenNews("true", { expires: 30 });
  };

  const resetNewsModal = () => {
    deleteNewsCookie();
    setShowModal(true);
  };

  return {
    showModal,
    handleCloseModal,
    resetNewsModal,
    hasSeenNews: hasSeenNews === "true",
  };
}
